/**
 * Deliberately conservative email validation - not a full RFC 5322
 * implementation (nothing short of actually sending a message reliably
 * validates an address anyway), just enough structural checking to catch
 * obvious typos and, more importantly, to reject anything that could be
 * used for header injection before it ever reaches a template or a
 * transport.
 */

/** Any control character, including CR/LF, is rejected outright - these are exactly what a header-injection attempt needs to smuggle in extra "To"/"Bcc" headers. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_PATTERN = /[\x00-\x1f\x7f]/;

/** One @, a non-empty local part, and a domain with at least one dot - intentionally simple and easy to audit. */
const EMAIL_SHAPE_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailAddress(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  if (trimmed !== value) return false; // reject leading/trailing whitespace rather than silently trimming it
  if (CONTROL_CHAR_PATTERN.test(trimmed)) return false;
  return EMAIL_SHAPE_PATTERN.test(trimmed);
}

/** Normalizes an already-validated address for use as a rate-limit/dedupe key - case-insensitive per the email spec's common-practice domain handling. */
export function normalizeEmailKey(email: string): string {
  return email.trim().toLowerCase();
}
